import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { BookOpen, Search, FileText, Users, ChevronRight, Clock, User, CheckCircle2, AlertCircle, Lightbulb, ArrowLeft, Printer, Calendar, Plus, Settings, Edit2, Trash2, Loader2, Eye, Link2, ExternalLink, Download, Upload } from 'lucide-react';
import { ICON_MAP, toEmbeddableUrl } from './documentLibraryConstants';
import { CategoryManagerDialog, DocumentEditorDialog, DeleteDocumentDialog } from './DocumentLibraryEditors';

export default function DocumentLibraryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);

  const [showCatMgr, setShowCatMgr] = useState(false);
  const [editorState, setEditorState] = useState({ open: false, mode: 'create', docId: null });
  const [deleteDoc, setDeleteDoc] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        api.get('/doc-library/categories'),
        api.get('/doc-library/documents'),
      ]);
      setCategories(c.data.categories || []);
      setDocs(d.data.documents || []);
    } catch { toast.error('Failed to load document library'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const filteredDocs = useMemo(() => {
    return docs.filter(d => {
      if (activeCategory !== 'all' && d.category !== activeCategory) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return d.title.toLowerCase().includes(q) ||
        (d.summary || '').toLowerCase().includes(q) ||
        (d.tags || []).some(t => t.toLowerCase().includes(q));
    });
  }, [docs, search, activeCategory]);

  const openDoc = async (id) => {
    setLoadingDoc(true);
    try {
      const r = await api.get(`/doc-library/documents/${id}`);
      setSelectedDoc(r.data);
    } catch { toast.error('Failed to load document'); }
    finally { setLoadingDoc(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/doc-library/documents/${deleteDoc.id}`);
      toast.success('Deleted');
      setDeleteDoc(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setDeleteDoc(null); }
  };

  const handleExport = async () => {
    try {
      const r = await api.get('/doc-library/export');
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document-library-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.documents.length} documents, ${r.data.categories.length} categories`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    const mode = window.confirm(
      'Choose import mode:\n\n' +
      '• OK = MERGE (recommended): existing entries are updated, new ones added, nothing is deleted.\n' +
      '• Cancel = REPLACE: DELETES everything first, then imports from file.'
    ) ? 'merge' : 'replace';
    if (mode === 'replace' && !window.confirm('Really DELETE all existing documents and categories first?')) {
      return;
    }
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      bundle.mode = mode;
      const r = await api.post('/doc-library/import', bundle);
      const { categories: cs, documents: ds } = r.data;
      toast.success(`Import (${mode}): ${cs.created + cs.updated} categories, ${ds.created + ds.updated} docs`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Import failed — check file format');
    }
  };

  if (selectedDoc) {
    return (
      <DocumentView
        doc={selectedDoc}
        categories={categories}
        onBack={() => setSelectedDoc(null)}
        isAdmin={isAdmin}
        onEdit={() => setEditorState({ open: true, mode: 'edit', docId: selectedDoc.id })}
        onDelete={() => setDeleteDoc(selectedDoc)}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="document-library-page">
      {/* Hero */}
      <div className="relative rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-violet-900 text-white p-8 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-violet-500/10 -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-200 mb-2">
                <BookOpen className="w-3 h-3" />
                <span>Document Library</span>
              </div>
              <h1 className="text-3xl font-bold tracking-tight">Process &amp; Management Handbook</h1>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl">
                Step-by-step guides, SOPs and how-to articles for operating the Training Management platform.
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" className="text-xs bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" onClick={handleExport} data-testid="export-library-btn" title="Download all docs as JSON backup">
                  <Download className="w-3.5 h-3.5 mr-1.5" />Export
                </Button>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 h-8 text-xs font-medium bg-white/5 border border-white/20 text-white rounded-md hover:bg-white/15 cursor-pointer" data-testid="import-library-btn" title="Upload a previously-exported JSON bundle">
                  <Upload className="w-3.5 h-3.5" />Import
                  <input type="file" accept="application/json,.json" className="hidden" onChange={(e) => { handleImportFile(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                <Button variant="outline" size="sm" className="text-xs bg-white/5 border-white/20 text-white hover:bg-white/15 hover:text-white" onClick={() => setShowCatMgr(true)} data-testid="manage-categories-btn">
                  <Settings className="w-3.5 h-3.5 mr-1.5" />Categories
                </Button>
                <Button size="sm" className="text-xs bg-violet-500 hover:bg-violet-400 text-white" onClick={() => setEditorState({ open: true, mode: 'create', docId: null })} data-testid="add-document-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" />Add Document
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 mt-5">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search documents, tags, process steps..."
                className="pl-9 h-10 text-sm bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-md focus-visible:ring-violet-400"
                data-testid="doc-library-search"
              />
            </div>
            <Badge variant="outline" className="text-[10px] border-white/20 text-slate-200 bg-white/5 px-2.5 py-1">
              {docs.length} documents &middot; {categories.length} categories
            </Badge>
          </div>
        </div>
      </div>

      {/* Category pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors ${activeCategory === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="cat-pill-all"
        >
          All ({docs.length})
        </button>
        {categories.map(c => {
          const count = docs.filter(d => d.category === c.id).length;
          const Icon = ICON_MAP[c.icon] || FileText;
          const active = activeCategory === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCategory(c.id)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium flex items-center gap-1.5 transition-colors ${active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`cat-pill-${c.id}`}
            >
              <Icon className="w-3 h-3" />
              {c.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Documents grid */}
      {loading || loadingDoc ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 text-slate-300 animate-spin mx-auto" /></div>
      ) : filteredDocs.length === 0 ? (
        <Card className="border p-12 text-center">
          <FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No documents match your search.</p>
          {isAdmin && (
            <Button size="sm" className="mt-4 text-xs bg-violet-600 hover:bg-violet-700" onClick={() => setEditorState({ open: true, mode: 'create', docId: null })}>
              <Plus className="w-3 h-3 mr-1" />Create First Document
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map(doc => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              category={categories.find(c => c.id === doc.category)}
              isAdmin={isAdmin}
              onOpen={() => openDoc(doc.id)}
              onEdit={() => setEditorState({ open: true, mode: 'edit', docId: doc.id })}
              onDelete={() => setDeleteDoc(doc)}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <CategoryManagerDialog open={showCatMgr} onClose={() => setShowCatMgr(false)} categories={categories} onChanged={refresh} />
      <DocumentEditorDialog
        open={editorState.open}
        mode={editorState.mode}
        docId={editorState.docId}
        categories={categories}
        onClose={() => setEditorState({ open: false, mode: 'create', docId: null })}
        onSaved={() => {
          refresh();
          // refresh open doc if user is viewing it
          if (selectedDoc && editorState.docId === selectedDoc.id) openDoc(selectedDoc.id);
        }}
      />
      <DeleteDocumentDialog open={!!deleteDoc} doc={deleteDoc} onCancel={() => setDeleteDoc(null)} onConfirm={confirmDelete} />
    </div>
  );
}


function DocumentCard({ doc, category, isAdmin, onOpen, onEdit, onDelete }) {
  const CatIcon = category ? (ICON_MAP[category.icon] || FileText) : FileText;
  return (
    <Card
      className="group border p-0 overflow-hidden hover:shadow-md hover:border-violet-200 transition-all"
      data-testid={`doc-card-${doc.id}`}
    >
      <div className={`h-1.5 ${category?.accent || 'bg-slate-300'}`} />
      <div onClick={onOpen} className="p-5 cursor-pointer">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${category?.iconBg || 'bg-slate-100'} ${category?.iconColor || 'text-slate-600'}`}>
            <CatIcon className="w-4 h-4" />
          </div>
          <div className="flex items-center gap-1.5">
            {doc.doc_type === 'external_link' && (
              <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-violet-50 text-violet-700 border-violet-200"><Link2 className="w-2.5 h-2.5 mr-1" />Linked</Badge>
            )}
            <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{category?.name || 'Uncategorised'}</Badge>
          </div>
        </div>
        <h3 className="text-sm font-bold text-slate-900 leading-snug group-hover:text-violet-700 transition-colors">
          {doc.title}
        </h3>
        <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{doc.summary}</p>
        <div className="flex items-center gap-3 mt-4 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{doc.readMinutes} min read</span>
          <span className="flex items-center gap-1"><User className="w-3 h-3" />{doc.author || 'Unknown'}</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" />v{doc.version}</span>
        </div>
      </div>
      <div className="px-5 pb-4 flex items-center justify-between border-t pt-3 mt-0">
        <div className="flex gap-1 flex-wrap">
          {(doc.tags || []).slice(0, 3).map(t => (
            <span key={t} className="text-[9px] px-1.5 py-0.5 bg-slate-50 text-slate-500 rounded font-mono">#{t}</span>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Edit" onClick={onEdit} data-testid={`edit-doc-${doc.id}`}><Edit2 className="w-3 h-3 text-slate-400 hover:text-violet-500" /></Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Delete" onClick={onDelete} data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500" /></Button>
            </>
          )}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Open" onClick={onOpen}><ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all" /></Button>
        </div>
      </div>
    </Card>
  );
}


function DocumentView({ doc, categories, onBack, isAdmin, onEdit, onDelete }) {
  const cat = categories.find(c => c.id === doc.category);
  const CatIcon = cat ? (ICON_MAP[cat.icon] || FileText) : FileText;
  return (
    <div className="max-w-4xl mx-auto" data-testid="document-view">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 -ml-2" data-testid="doc-back-btn">
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back to Library
        </Button>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="text-xs h-8" onClick={onEdit} data-testid="doc-edit-btn">
              <Edit2 className="w-3.5 h-3.5 mr-1.5" />Edit
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={onDelete} data-testid="doc-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
            </Button>
          </div>
        )}
      </div>

      {/* Document header */}
      <Card className="border-0 shadow-sm p-0 overflow-hidden">
        <div className={`h-2 ${cat?.accent || 'bg-slate-300'}`} />
        <div className="p-8 border-b bg-gradient-to-b from-slate-50/50 to-white">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className={`text-[10px] ${cat?.iconBg} ${cat?.iconColor} border-transparent`}>
              <CatIcon className="w-3 h-3 mr-1" />{cat?.name || 'Uncategorised'}
            </Badge>
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-2.5 h-2.5 mr-1" />v{doc.version}
            </Badge>
            <Badge variant="outline" className="text-[10px] text-slate-500">
              <Clock className="w-2.5 h-2.5 mr-1" />{doc.readMinutes} min read
            </Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight leading-tight">{doc.title}</h1>
          <p className="text-sm text-slate-500 mt-3 leading-relaxed">{doc.summary}</p>
          <div className="flex items-center gap-4 mt-5 text-[11px] text-slate-500 flex-wrap">
            <span className="flex items-center gap-1.5"><User className="w-3 h-3" /><span className="font-medium text-slate-700">{doc.author || 'Unknown'}</span></span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />Updated {doc.updatedAt}</span>
            {doc.audience && doc.audience.length > 0 && (
              <span className="flex items-center gap-1.5"><Users className="w-3 h-3" />For: {doc.audience.join(', ')}</span>
            )}
          </div>
        </div>

        {/* Table of Contents — only for authored docs */}
        {doc.doc_type !== 'external_link' && doc.sections && doc.sections.length > 2 && (
          <div className="px-8 py-5 border-b bg-slate-50/50">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">On this page</p>
            <ol className="space-y-1.5">
              {doc.sections.map((s, i) => (
                <li key={i} className="text-xs">
                  <a href={`#${doc.id}-s-${i}`} className="text-violet-600 hover:text-violet-800 hover:underline inline-flex items-center gap-1.5">
                    <span className="text-slate-400 font-mono">{String(i + 1).padStart(2, '0')}</span>
                    {s.heading}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Body */}
        {doc.doc_type === 'external_link' ? (
          <div className="p-6 bg-slate-50/40">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Link2 className="w-3.5 h-3.5 text-violet-500" />
                <span className="font-medium">External resource</span>
                <span className="font-mono text-[11px] text-slate-400 truncate max-w-md">{doc.external_url}</span>
              </div>
              <a
                href={doc.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-violet-600 hover:text-violet-800 flex items-center gap-1"
                data-testid="doc-open-external"
              >
                Open in new tab <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <iframe
              src={toEmbeddableUrl(doc.external_url)}
              className="w-full h-[75vh] border border-slate-200 rounded-md bg-white"
              title={doc.title}
              data-testid="doc-embedded-iframe"
            />
            <p className="text-[10px] text-slate-400 mt-2 text-center">
              If the preview is blank, the source may block embedding — use "Open in new tab" above.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[70vh]">
            <div className="p-8 space-y-10">
              {(doc.sections || []).map((s, i) => (
                <DocSection key={i} section={s} anchor={`${doc.id}-s-${i}`} index={i} />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="px-8 py-5 border-t bg-slate-50/40 flex items-center justify-between">
          <div className="flex gap-1.5 flex-wrap">
            {(doc.tags || []).map(t => (
              <span key={t} className="text-[10px] px-2 py-0.5 bg-white border border-slate-200 text-slate-500 rounded-full font-mono">#{t}</span>
            ))}
          </div>
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => window.print()} data-testid="doc-print-btn">
            <Printer className="w-3.5 h-3.5 mr-1.5" />Print / Export PDF
          </Button>
        </div>
      </Card>
    </div>
  );
}


function DocSection({ section, anchor, index }) {
  return (
    <div id={anchor} className="scroll-mt-8">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex-shrink-0 w-7 h-7 rounded-md bg-violet-100 text-violet-700 flex items-center justify-center text-[11px] font-bold mt-0.5">
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-900 leading-tight">{section.heading}</h2>
          {section.subheading && <p className="text-xs text-slate-500 mt-0.5">{section.subheading}</p>}
        </div>
      </div>
      <div className="pl-10 space-y-4">
        {(section.blocks || []).map((b, j) => <Block key={j} block={b} />)}
      </div>
    </div>
  );
}


function Block({ block }) {
  if (block.type === 'paragraph') {
    return <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{block.text}</p>;
  }
  if (block.type === 'steps') {
    return (
      <ol className="space-y-3">
        {(block.items || []).map((item, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
            <div className="flex-1 pt-0.5">
              <p className="text-sm font-semibold text-slate-900">{item.title}</p>
              {item.detail && <p className="text-xs text-slate-600 mt-0.5 leading-relaxed whitespace-pre-wrap">{item.detail}</p>}
              {item.hint && (
                <p className="text-[11px] text-violet-600 mt-1 flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" />{item.hint}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    );
  }
  if (block.type === 'bullets') {
    return (
      <ul className="space-y-2">
        {(block.items || []).map((t, i) => (
          <li key={i} className="text-sm text-slate-700 flex gap-2 leading-relaxed">
            <span className="text-violet-400 mt-1 flex-shrink-0">&bull;</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'callout') {
    const tones = {
      info:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-900',    icon: AlertCircle,     iconColor: 'text-blue-500' },
      success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', icon: CheckCircle2,    iconColor: 'text-emerald-500' },
      warning: { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   icon: AlertCircle,     iconColor: 'text-amber-500' },
      tip:     { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-900',  icon: Lightbulb,       iconColor: 'text-violet-500' },
    };
    const t = tones[block.tone] || tones.info;
    const Icon = t.icon;
    return (
      <div className={`rounded-md border ${t.border} ${t.bg} px-4 py-3 flex gap-3`}>
        <Icon className={`w-4 h-4 ${t.iconColor} flex-shrink-0 mt-0.5`} />
        <div className={`text-xs ${t.text} leading-relaxed`}>
          {block.title && <p className="font-semibold mb-0.5">{block.title}</p>}
          <p className="whitespace-pre-wrap">{block.text}</p>
        </div>
      </div>
    );
  }
  if (block.type === 'table') {
    return (
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b">
            <tr>
              {(block.headers || []).map((h, i) => (
                <th key={i} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(block.rows || []).map((r, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-slate-50/50">
                {r.map((c, j) => (
                  <td key={j} className="px-3 py-2 text-slate-700">{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === 'faq') {
    return (
      <div className="space-y-3">
        {(block.items || []).map((q, i) => (
          <div key={i} className="border rounded-md overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b">
              <p className="text-xs font-semibold text-slate-900">Q. {q.question}</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{q.answer}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (block.type === 'screenshot-placeholder') {
    return (
      <div className="rounded-md border-2 border-dashed border-slate-200 bg-slate-50/50 p-6 flex flex-col items-center justify-center text-center">
        <Eye className="w-6 h-6 text-slate-300 mb-2" />
        <p className="text-xs font-medium text-slate-500">Screenshot: {block.caption}</p>
        {block.hint && <p className="text-[10px] text-slate-400 mt-1">{block.hint}</p>}
      </div>
    );
  }
  return null;
}
